import { useApiHandler } from '@/hooks/useApiHandler'
import { ApiConfig, ApiResponse, ErrorHandlingStrategy } from '@/types/api'
import { AspirantDocumentsResponse } from '@/types/documents'
import {
    ConfirmUploadResult,
    FileResponse,
    PresignUploadRequest,
    PresignUploadResponse,
    UpdateFileRequest,
    UploadResponse
} from '@/types/files'
import { HomologationStudyDto } from '@/types/homologation'
import { ApiRoutes } from './apiRoutes'

// Margen de seguridad bajo el límite real de 10MB del API Gateway
const GATEWAY_SAFE_UPLOAD_LIMIT_BYTES = 9 * 1024 * 1024

export function useFilesApi(config?: Partial<ApiConfig>) {
    // Default configuration with error handling
    const defaultConfig: Partial<ApiConfig> = {
        errorHandling: ErrorHandlingStrategy.RETURN,
        retryAttempts: 1,
        ...config
    }

    const api = useApiHandler(ApiRoutes.FILES, defaultConfig)

    const apiOnbase = useApiHandler(
        `${ApiRoutes.INTEGRATIONS}/onbase-integrations/api`,
        defaultConfig
    )

    return {
        uploadOnBaseFile: (formData: FormData) =>
            apiOnbase.post<UploadResponse>('/Document', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            }),

        createAspirantDocument: (requestAspiranDocumentCreateBody: {
            idAspirant: number
            archivalDocumentTypeId: number
            documentUrl: string
            idInscription: string
            isActive: boolean
            idDocumentStatus: number
            createdAt: string
            idOnbase: number
        }) =>
            api.post<FileResponse>(
                '/aspirant-documents',
                requestAspiranDocumentCreateBody
            ),

        getDocumentInfo: (fileId: string) =>
            api.get<FileResponse>(`/aspirant-documents/${fileId}/2/true`),

        getFile: (fileId: string) =>
            apiOnbase.get<Blob>(`/Document/${fileId}/convertirPdf/true`, {
                responseType: 'blob'
            }),

        updateFile: (fileId: string, data: UpdateFileRequest) =>
            api.put<FileResponse>(`/${fileId}`, data),

        deleteFile: (fileId: string) =>
            apiOnbase.delete<void>(`/Document/${fileId}`),

        softDeleteFile: (fileId: string) =>
            api.patch<void>(
                `/aspirant-documents/${fileId}/status?isActive=false`
            ),
        // Document specific endpoints
        uploadAspirantDocument: (
            aspirantId: number,
            documentTypeId: number,
            file: File
        ) => {
            const formData = new FormData()

            formData.append('file', file)

            return api.post<FileResponse>(
                `/aspirants/${aspirantId}/documents/${documentTypeId}`,
                formData
            )
        },

        updateAspirantDocument: (
            documentId: number,
            data: UpdateFileRequest,
            file?: File
        ) => {
            const formData = new FormData()

            if (file) {
                formData.append('file', file)
            }
            if (data) {
                formData.append('data', JSON.stringify(data))
            }

            return api.put<FileResponse>(
                `/aspirant-documents/${documentId}`,
                formData
            )
        },

        // Homologation specific endpoints
        getHomologationProcess: (
            processId: number,
            includeDocuments: boolean = false
        ) =>
            api.get<HomologationStudyDto[]>(`/homologation/${processId}`, {
                params: { includeDocuments }
            }),

        uploadHomologationDocument: (homologationId: number, file: File) => {
            const formData = new FormData()

            formData.append('file', file)

            return api.post<FileResponse>(
                `/homologation/${homologationId}/documents`,
                formData
            )
        },

        getAspirantDocuments: (
            inscriptionId: number | null
        ): Promise<ApiResponse<AspirantDocumentsResponse>> =>
            api.get<AspirantDocumentsResponse>(
                `/aspirant-documents/by-inscription?idInscription=${inscriptionId}`
            ),

        // Crear nuevos documentos adicionales de homologación para una inscripción
        createHomologationAdditionalDocuments: (
            idInscription: number
        ): Promise<ApiResponse<any>> =>
            api.post<any>(
                `/aspirant-documents/homologation/additional-documents`,
                undefined,
                { params: { idInscription } }
            ),

        // Inactivar documentos adicionales de homologación por índice de origen
        inactivateHomologationAdditionalDocuments: (
            idInscription: number,
            originIndex: number
        ): Promise<ApiResponse<any>> =>
            api.patch<any>(
                `/aspirant-documents/homologation/additional-documents/inactivate`,
                undefined,
                { params: { idInscription, originIndex } }
            ),

        // Método para subir documentos directamente a S3
        uploadAspirantDocumentToS3: async (
            aspirantId: number,
            additionalDocumentId: number | null,
            sapDocumentSubtestId: number | null,
            idInscription: number,
            file: File,
            agent: number,
            markAsPending: boolean = false,
            originIndex?: number
        ) => {
            if (file.size <= GATEWAY_SAFE_UPLOAD_LIMIT_BYTES) {
                // Archivo pequeño: ruta existente, va directo por el API Gateway
                const formData = new FormData()

                formData.append('file', file)

                return api.post<FileResponse>(
                    `/aspirant-documents/upload-async`,
                    formData,
                    {
                        params: {
                            aspirantId,
                            additionalDocumentId,
                            sapDocumentSubtestId,
                            idInscription,
                            bypassed: true,
                            markAsPending: markAsPending,
                            // originIndex enviado si está disponible
                            ...(originIndex !== undefined && { originIndex })
                        },
                        headers: {
                            'Content-Type': 'multipart/form-data',
                            Agent: agent
                        }
                    }
                )
            }

            // Archivo grande: el Gateway limita el body a 10MB, así que se sube
            // directo a S3 con una URL prefirmada (presign -> PUT -> confirm)
            const presignRes = await api.post<PresignUploadResponse>(
                '/aspirant-documents/upload-async/presign',
                {
                    idInscription,
                    sapDocumentSubtestId,
                    additionalDocumentId,
                    originIndex,
                    bypassed: true,
                    markAsPending,
                    fileName: file.name,
                    contentType: file.type,
                    fileSize: file.size
                } as PresignUploadRequest,
                { headers: { Agent: agent } }
            )

            if (presignRes.status !== 200) {
                return presignRes
            }

            const { uploadToken, uploadUrl, requiredContentType } =
                presignRes.data

            // PUT directo a S3: nunca a través de `api` (ApiHandler agregaría
            // Authorization y el baseURL del backend, rompiendo la firma de la URL)
            const putResponse = await fetch(uploadUrl, {
                method: 'PUT',
                headers: { 'Content-Type': requiredContentType },
                body: file
            })

            if (!putResponse.ok) {
                throw new Error(
                    `Fallo al subir el archivo a S3 (status ${putResponse.status})`
                )
            }

            return api.post<ConfirmUploadResult>(
                '/aspirant-documents/upload-async/confirm',
                { uploadToken },
                { headers: { Agent: agent } }
            )
        }
    }
}
